import _ from 'underscore';

import * as ast from 'notebook/tables/excess/AST';
import tokenizer from 'notebook/tables/excess/Tokenizer';
import {Token, TYPES} from 'notebook/tables/excess/Tokenizer';
import * as util from 'notebook/tables/excess/Util';

// Takes a list of tokens, and returns a single token representing the parse tree.
function reduce(tokens: Array<Token>): Token & {node: ast.ASTNode} {
  tokens = parseFunctions(tokens);
  tokens = removeSpaces(tokens);
  tokens = parseParenthesis(tokens);
  tokens = parseUnary(tokens);
  tokens = parseBinary(tokens, [ast.BinaryOperatorType.EXPONENTIAL]);
  tokens = parseBinary(tokens, [ast.BinaryOperatorType.MULTIPLICATION, ast.BinaryOperatorType.DIVISION]);
  tokens = parseBinary(tokens, [ast.BinaryOperatorType.ADDITION, ast.BinaryOperatorType.SUBTRACTION]);
  tokens = parseBinary(tokens, [
    ast.BinaryOperatorType.EQUALS,
    ast.BinaryOperatorType.NOT_EQUALS,
    ast.BinaryOperatorType.GREATER_THAN_OR_EQUAL,
    ast.BinaryOperatorType.GREATER_THAN,
    ast.BinaryOperatorType.LESS_THAN_OR_EQUAL,
    ast.BinaryOperatorType.LESS_THAN,
  ]);

  const token: Token | null = tokens.length === 1 ? tokens[0] : null;
  if (token == null || !token.isASTNode()) {
    throw new util.FormulaParseError('Invalid formula');
  }
  return token;
}

function parseUnary(tokens: Array<Token>): Array<Token> {
  const ret: Array<Token> = [];

  // Process tokens from right to left since the unary operator closest to the operand takes precedence.
  for (let index = tokens.length - 1; index >= 0; index--) {
    const token = tokens[index];
    const prevToken = tokens[index - 1] as Token | undefined;
    const nextToken = ret[0] as Token | undefined;

    // We tokenize +/- with ast.BinaryOperator, but contextually it might actually be a UnaryOperator
    const isPotentialUnary =
      // eslint-disable-next-line no-restricted-syntax
      Array.from(_.values(ast.UnaryOperatorType)).includes(token.text as ast.UnaryOperatorType) &&
      token.isASTNode() &&
      !token.node.isTerminal();
    // If the token is found after a non-terminal node, it is unary.
    const isExpressionStart = prevToken == null || (prevToken.isASTNode() && !prevToken.node.isTerminal());
    // If we are to absorb the next token as a subexpression, it better be complete.
    const isBeforeTerminal = nextToken != null && nextToken.isASTNode() && nextToken.node.isTerminal();

    if (isPotentialUnary && isExpressionStart) {
      if (!isBeforeTerminal) {
        throw new util.FormulaParseError('No argument after operator.');
      }
      token.node = new ast.UnaryOperator({type: token.text as ast.UnaryOperatorType, child: nextToken.node});
      ret.shift(); // remove nextToken as it has just been absorbed into token
    }
    ret.unshift(token);
  }

  return ret;
}

// Takes a list of tokens, and converts all binary expressions in types to a single token
// representing the expression tree.
function parseBinary(tokens: Array<Token>, types: Array<ast.BinaryOperatorType> = []): Array<Token> {
  const ret: Array<Token> = [];
  let index = 0;
  while (index < tokens.length) {
    const token = tokens[index];
    if (
      token.isASTNode() &&
      token.node instanceof ast.BinaryOperator &&
      Array.from(types).includes(token.node.type) &&
      !token.node.isTerminal()
    ) {
      if (ret.length === 0) {
        throw new util.FormulaParseError('No argument before operator.');
      }
      if (index + 1 >= tokens.length) {
        throw new util.FormulaParseError('No argument after operator.');
      }

      const previous = ret.pop()!;
      const next = tokens[index + 1];
      if (previous.isASTNode() && previous.node.isTerminal() && next.isASTNode() && next.node.isTerminal()) {
        token.node.left = previous.node;
        token.node.right = next.node;
        ret.push(token);
      } else {
        throw new util.FormulaParseError('Number value expected for operator');
      }
      index += 2;
    } else {
      ret.push(tokens[index]);
      index += 1;
    }
  }
  return ret;
}

// Takes a list of tokens, and converts all parenthetical expressions in types to a single token
// representing the expression tree.
function parseParenthesis(tokens: Array<Token>): Array<Token> {
  const ret = [];
  let index = 0;
  while (index < tokens.length) {
    if (tokens[index].isLeftParen()) {
      const lastIndex = matchingParen(tokens, index);
      // eslint-disable-next-line benchlint/no-eqeqeq-null
      if (lastIndex === null) {
        throw new util.FormulaParseError('Mismatching parenthesis');
      }
      const token = reduce(tokens.slice(index + 1, lastIndex));
      ret.push(token);
      index = lastIndex + 1;
    } else if (tokens[index].isRightParen()) {
      throw new util.FormulaParseError('Mismatching parenthesis');
    } else {
      ret.push(tokens[index]);
      index += 1;
    }
  }
  return ret;
}

// Takes a list of tokens, and returns a list without any spaces.
function removeSpaces(tokens: Array<Token>): Array<Token> {
  const ret = [];
  let index = 0;
  while (index < tokens.length) {
    if (!tokens[index].isSpace()) {
      ret.push(tokens[index]);
    }
    index += 1;
  }
  return ret;
}

// Takes a list of tokens, and converts all functions in types to a single token
// representing the function call.
function parseFunctions(tokens: Array<Token>): Array<Token> {
  const ret = [];
  let index = 0;
  while (index < tokens.length) {
    // Because we parseFunctions first in reduce, any subsequent call to reduce (e.g. parseParentheses if you
    // wrap a function in parentheses) will always encounter an  already terminal Function, which does not
    // need to parsed anymore, so we skip over such terminal Functions nodes.
    const token = tokens[index];
    if (token.isASTNode() && token.node instanceof ast.FunctionOperator && !token.node.isTerminal()) {
      let expression;

      if (tokens[index + 1].type !== TYPES.LEFT_PAREN) {
        throw new util.FormulaParseError('Function must be followed by a left parenthesis.');
      }
      const lastIndex = matchingParen(tokens, index + 1);
      // eslint-disable-next-line benchlint/no-eqeqeq-null
      if (lastIndex === null) {
        throw new util.FormulaParseError('No matching parenthesis found for function.');
      }

      const args: Array<ast.ASTNode> = [];
      let curTokenList = [];
      index += 2; // Start after the parenthesis
      let nestLevel = 0;
      while (index < lastIndex) {
        if (tokens[index].isComma() && nestLevel === 0) {
          if (curTokenList.length === 0) {
            throw new util.FormulaParseError('Empty argument to function.');
          }
          expression = reduce(curTokenList);
          args.push(expression.node);
          curTokenList = [];
        } else {
          if (tokens[index].isLeftParen()) {
            nestLevel += 1;
          }
          if (tokens[index].isRightParen()) {
            nestLevel -= 1;
          }
          curTokenList.push(tokens[index]);
        }
        index += 1;
      }

      // Functions such as TRUE and NULL expect no arguments
      if (token.text.match(util.ZERO_ARGUMENT_FUNCTIONS_REGEX)) {
        if (curTokenList.length !== 0) {
          throw new util.FormulaParseError('No arguments should be passed to function.');
        }
      } else {
        // get the last argument (which isn't followed by a comma)
        if (curTokenList.length === 0) {
          throw new util.FormulaParseError('Empty argument to function.');
        }
        expression = reduce(curTokenList);
        args.push(expression.node);
      }

      index += 1; // Skip the last parenthesis
      token.node.children = args;

      ret.push(token);
    } else {
      ret.push(tokens[index]);
      index += 1;
    }
  }
  return ret;
}

// Returns the index of the matching right paren, or null if it doesn't exist.
function matchingParen(tokens: Array<Token>, index: number): number | null {
  let count = 1;
  index += 1;
  while (index < tokens.length) {
    if (tokens[index].isLeftParen()) {
      count += 1;
    } else if (tokens[index].isRightParen()) {
      count -= 1;
      if (count === 0) {
        return index;
      }
    }
    index += 1;
  }
  return null;
}

function parseAsValue(text: string): ast.Value {
  return new ast.Value(util.parseValue(text));
}

function parse(text: string): ast.ASTNode {
  return parseWithTokenValidation(text, () => {});
}

function parseWithoutCellRefs(text: string): ast.ASTNode {
  // TODO(TypeScript): Remove and replace with Token type
  interface PartialToken {
    isCellRef(): boolean;
  }

  return parseWithTokenValidation(text, (tokens: Array<PartialToken>) => {
    if (tokens.some((t) => t.isCellRef())) {
      throw new util.FormulaParseError('This cell cannot reference other cells');
    }
  });
}

function parseWithTokenValidation(
  text: string,
  validateTokens: (tokens: Array<unknown>) => void
): ast.ASTNode {
  const formulaText = util.getFormulaText(text);
  if (formulaText != null) {
    const tokens = tokenizer.tokenize(formulaText);
    validateTokens(tokens);
    const token = reduce(tokens);
    if (!token.node.isSingular()) {
      throw new util.FormulaParseError('Invalid formula');
    }
    return token.node;
  }

  // If the text of a cell does not start with `=`, the value is still implicitly parsed for a numeric value.
  // Returns a numeric ASTValue if a number was parsed, otherwise a textual one.
  return parseAsValue(text);
}

/**
 * Try to parse the text. Return the node in case of success and null in case of failure.
 */
function tryParse(text: string): ast.ASTNode | null {
  try {
    return parse(text);
  } catch (e) {
    if (e instanceof util.FormulaParseError || e instanceof util.FormulaTokenizeError) {
      // Failed to parse the formula
      return null;
    } else {
      throw e;
    }
  }
}

export default {
  parse,
  parseAsValue,
  parseWithoutCellRefs,
  tryParse,
  _reduce: reduce, // for testing
};
